import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AppTopbar } from "../../../shell/AppTopbar";
import {
  Page,
  Table,
  type Column,
  Badge,
  Text,
  DateTimeField,
  TextField,
  Button,
  Input,
  Select,
  FilterField,
  FilterPanel,
  EmptyState,
} from "@marekl/adminer/components";
import {
  useConfirm,
  useDebouncedSearchFilter,
  useTableSort,
  useUrlFilters,
} from "@marekl/adminer/hooks";
import {
  useUsers,
  useStartImpersonation,
  isAdmin as userIsAdmin,
} from "../../../auth/queries";
import type { SystemRole, User } from "../../../auth/queries";
import { useAuth } from "../../../auth/useAuth";
import { ROLE_LABEL } from "../../../lib/i18n/roles";
import { AssignmentChips, EditUserModal, ResetPasswordModal, CreateUserModal } from "./UserModals";
import { ManageRolesModal } from "./ManageRolesModal";

const ROLE_FILTER_OPTIONS: SystemRole[] = [
  "admin",
  "members_admin",
  "members_viewer",
  "competitions_admin",
  "competitions_viewer",
  "global_viewer",
  "federation_admin",
  "judge",
  "custom_pages_manager",
  "notices_manager",
  "team_leader",
  "coach",
  "assistant",
  "athlete",
];

type ModalState =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; user: User }
  | { kind: "reset"; user: User }
  | { kind: "roles"; user: User };

export function UsersPage() {
  const { data: users = [], isLoading, error, refetch } = useUsers();
  const { user: currentUser } = useAuth();
  const confirm = useConfirm();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  // Destructured: mutateAsync is a stable reference, so the memoised columns
  // below do not re-sort when the mutation changes state.
  const { mutateAsync: startImpersonation } = useStartImpersonation();

  const currentIsAdmin = userIsAdmin(currentUser);
  const alreadyImpersonating = !!currentUser?.impersonator;

  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") ?? "";
  const roleFilter = (searchParams.get("role") ?? "") as SystemRole | "";

  // The list is unpaginated, but useUrlFilters still owns the write: its
  // predecessor here mutated and returned the very URLSearchParams instance
  // the router handed it, which React Router treats as unchanged.
  const { setFilter } = useUrlFilters({
    searchParams,
    setSearchParams,
    paginationKey: "page",
  });

  const { draft: qDraft, setDraft: setQDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => setFilter("q", next),
  });

  const filteredUsers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      if (needle) {
        const hay = `${u.email} ${u.display_name}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (roleFilter) {
        if (!u.assignments.some((a) => a.role === roleFilter)) return false;
      }
      return true;
    });
  }, [users, q, roleFilter]);

  const total = filteredUsers.length;
  const countLabel = `${total} ${
    total === 1 ? "účet" : total >= 2 && total <= 4 ? "účty" : "účtů"
  }`;
  const metaSuffix =
    total !== users.length ? ` (z ${users.length})` : "";

  function closeModal() {
    setModal({ kind: "closed" });
  }

  // useCallback because the columns are memoised for useTableSort: a
  // function identity that changed every render would re-sort every render.
  const canImpersonate = useCallback(
    (row: User): boolean => {
      if (!currentIsAdmin) return false;
      if (alreadyImpersonating) return false;
      if (!currentUser || row.id === currentUser.id) return false;
      if (!row.active) return false;
      if (row.password_locked) return false;
      if (userIsAdmin(row)) return false;
      return true;
    },
    [currentIsAdmin, alreadyImpersonating, currentUser],
  );

  // useCallback because the columns are memoised for useTableSort. The dialog
  // shows a refusal itself, so there is nothing left to toast.
  const askImpersonate = useCallback(
    (u: User) =>
      confirm({
        title: "Vystupovat za uživatele",
        description: (
          <>
            Začnete vystupovat za <strong>{u.display_name || u.email}</strong>. Všechny
            vaše akce budou zaznamenány s vaší identitou jako vystupujícího
            administrátora. Vystupování ukončíte tlačítkem v horní liště.
          </>
        ),
        confirmLabel: "Vystupovat za uživatele",
        onConfirm: async () => {
          await startImpersonation(u.id);
        },
      }),
    [confirm, startImpersonation],
  );

  // Hoisted for useTableSort, which re-sorts on the columns identity.
  const columns = useMemo<Column<User>[]>(
    () => [
            {
              key: "email",
              header: "E-mail",
              sortable: true,
              sortValue: (u) => u.email,
              render: (u) => <Text variant="mono">{u.email}</Text>,
            },
            {
              key: "display_name",
              header: "Jméno",
              sortable: true,
              // An account with no display name is missing, not an empty
              // string that would sort to the front.
              sortValue: (u) => u.display_name || null,
              render: (u) => <TextField record={u} source="display_name" />,
            },
            {
              key: "assignments",
              header: "Role",
              render: (u) => <AssignmentChips assignments={u.assignments} />,
            },
            {
              key: "active",
              header: "Stav",
              sortable: true,
              sortValue: (u) => u.active,
              render: (u) =>
                u.active ? (
                  <Badge variant="ok">Aktivní</Badge>
                ) : (
                  <Badge variant="muted">Deaktivován</Badge>
                ),
            },
            {
              key: "last_login_at",
              header: "Posl. přihlášení",
              sortable: true,
              // Never logged in is missing, so it sorts last in both
              // directions rather than reading as the oldest login.
              sortValue: (u) => u.last_login_at ?? null,
              render: (u) => <DateTimeField record={u} source="last_login_at" />,
            },
            // Čtení všeho reads the accounts and changes none of them.
            ...(currentIsAdmin
              ? [
                {
                  key: "actions",
                  header: "",
                  render: (u: User) => (
                    <Table.Actions>
                      <Table.Action
                        appearance="button"
                        onClick={() => setModal({ kind: "edit", user: u })}
                      >
                        Upravit
                      </Table.Action>
                      <Table.Action
                        appearance="button"
                        onClick={() => setModal({ kind: "roles", user: u })}
                      >
                        Role
                      </Table.Action>
                      <Table.Action
                        appearance="button"
                        onClick={() => setModal({ kind: "reset", user: u })}
                      >
                        Heslo
                      </Table.Action>
                      {canImpersonate(u) && (
                        <Table.Action
                          appearance="button"
                          onClick={() => askImpersonate(u)}
                        >
                          Vystupovat za…
                        </Table.Action>
                      )}
                    </Table.Actions>
                  ),
                },
                ]
              : []),
    ],
    [canImpersonate, askImpersonate, currentIsAdmin],
  );

  const tableSort = useTableSort(filteredUsers, {
    columns,
    searchParams,
    setSearchParams,
    defaultSort: { key: "email", dir: "asc" },
  });

  return (
    <>
      <AppTopbar crumbs={[{ label: "Uživatelé" }]} />
      <Page>
        <Page.Header
          title="Uživatelé"
          subtitle="správa uživatelských účtů"
          actions={
            currentIsAdmin ? (
              <Button variant="primary" onClick={() => setModal({ kind: "create" })}>
                Nový uživatel
              </Button>
            ) : undefined
          }
        />
        <Page.Content>
          <FilterPanel>
            <FilterField>
              <Input
                label="Hledat"
                placeholder="E-mail nebo jméno"
                value={qDraft}
                onChange={(e) => setQDraft(e.target.value)}
              />
            </FilterField>
            <FilterField>
              <Select
                label="Role"
                value={roleFilter}
                onChange={(e) => setFilter("role", e.target.value)}
              >
                <option value="">Všechny role</option>
                {ROLE_FILTER_OPTIONS.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </Select>
            </FilterField>
          </FilterPanel>

          <Page.SectionHeader
            title="Uživatelé systému"
            meta={`${countLabel}${metaSuffix}`}
          />

          <Table<User>
            framed
            {...tableSort.tableProps}
            rowKey={(u) => u.id}
            columns={columns}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            empty={
              <EmptyState
                title={users.length === 0 ? "Žádní uživatelé" : "Nic neodpovídá filtru"}
                description={
                  users.length === 0
                    ? "V systému zatím není žádný uživatelský účet."
                    : "Zkuste hledání nebo filtr role zúžit jinak."
                }
              />
            }
          />
        </Page.Content>
      </Page>

      {modal.kind === "edit" && (
        <EditUserModal
          user={modal.user}
          onClose={closeModal}
        />
      )}

      {modal.kind === "create" && (
        <CreateUserModal onClose={closeModal} />
      )}

      {modal.kind === "reset" && (
        <ResetPasswordModal
          user={modal.user}
          onClose={closeModal}
        />
      )}

      {modal.kind === "roles" && (
        <ManageRolesModal
          user={modal.user}
          onClose={closeModal}
        />
      )}

    </>
  );
}
